/**
 * `/season/:team_id` — read-only season view for the captain SPA
 * (web UI Phase 2 chunk 6/6).
 *
 * One round-trip to `GET /api/team/overview` and we render everything
 * the captain needs to assess the state of the team: upstream health,
 * upcoming matches, pending lineups, scores overdue, and the 5 most
 * recent scouting briefs. Editing-heavy flows (propose_lineup,
 * approve_lineup, send_calendar_invites, etc.) live in later chunks.
 *
 * Scouting briefs render via `<details>/<summary>` disclosure with
 * the markdown body wrapped in `<pre>` — Phase 2 chunk 6 spec calls
 * this out explicitly as the "cheap; format upgrade in a future chunk"
 * shape. Adding a markdown renderer ships ~12 KB of JS for a feature
 * captains tap into rarely; the deferral is intentional.
 *
 * The `team_id` URL segment is a sanity check, not an authorization
 * dimension. The server scopes the overview to the calling captain's
 * team_id (from the JWT); if a captain shares their URL with another
 * captain, the other captain sees their OWN team's data, not the
 * URL's. We surface a soft mismatch banner if URL ≠ session team_id
 * so the captain isn't confused why bookmarks don't carry context.
 */

import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ApiError, apiGet } from "../lib/api";
import { useAuthedSession } from "../components/RequireAuth";

// ─── Types — mirror the server `TeamOverviewResponse` shape ────────────

type HealthState = "ok" | "degraded" | "down";

type ActiveTeam = {
  team_id: string;
  name: string;
  league: "WTTA" | "USTA";
  players: number;
  next_match: string | null;
};

type UpcomingMatch = {
  match_id: string;
  date: string;
  opponent: string | null;
  lineup_state: "none" | "draft" | "approved" | "locked";
  rsvps_outstanding: number;
};

type ScoreOverdue = { match_id: string; hours_overdue: number };

type RecentBrief = {
  match_id: string;
  opponent: string | null;
  generated_at: string;
  body_md: string;
};

type OverviewResponse = {
  ok: true;
  generated_at: string;
  active_teams: ActiveTeam[];
  upcoming_matches_14d: UpcomingMatch[];
  lineups_pending_approval: { match_id: string; proposed_at: string }[];
  rsvps_outstanding: { match_id: string; players_no_reply: number }[];
  scores_overdue: ScoreOverdue[];
  health: {
    wtta: HealthState;
    tennislink: HealthState;
    schmidt: HealthState;
    imessage: HealthState;
    supabase: HealthState;
  };
  recent_scouting_briefs: RecentBrief[];
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; data: OverviewResponse }
  | { kind: "error"; message: string };

// ─── Helpers ───────────────────────────────────────────────────────────

function formatMatchDate(iso: string): string {
  // Compact human format: "Thu May 28, 6:30 PM". Locale-respecting via
  // toLocaleString. Falls back to the raw ISO if Date construction fails
  // (defensive — a malformed match_date is a server-side bug worth
  // surfacing rather than crashing the page).
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function healthBadge(label: string, state: HealthState): string {
  const tag = state === "ok" ? "ok" : state === "degraded" ? "⚠ degraded" : "⚠ down";
  return `${label} ${tag}`;
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function Season() {
  const session = useAuthedSession();
  const { team_id: routeTeamId } = useParams<{ team_id: string }>();

  // Hooks must run unconditionally — declare BEFORE any early return. We
  // bounce to the canonical URL via <Navigate> below if `team_id` is
  // missing; the hook state populated in the meantime is harmless.
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const data = await apiGet<OverviewResponse>("/api/team/overview");
        if (cancelled) return;
        setState({ kind: "ok", data });
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiError
            ? e.status === 404
              ? "Season view backend not yet available. (Phase 2 in progress.)"
              : `Failed to load (${e.status}).`
            : "Network error. Check your connection and retry.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // No team_id in the URL → redirect to the canonical /season/:team_id
  // from the session. Hit /season directly (no param) → bounce.
  if (!routeTeamId) {
    return <Navigate to={`/season/${session.team_id}`} replace />;
  }

  const urlMismatch = routeTeamId !== session.team_id;

  if (state.kind === "loading") {
    return (
      <section>
        <p className="muted">Loading season…</p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section>
        <h1>Season</h1>
        <p role="alert" style={{ color: "#b3261e" }}>
          {state.message}
        </p>
      </section>
    );
  }

  const data = state.data;
  const team = data.active_teams[0] ?? null;
  const teamName = team?.name ?? "Your team";
  const h = data.health;
  const soonestPending =
    data.lineups_pending_approval.length > 0
      ? [...data.lineups_pending_approval].sort((a, b) =>
          a.proposed_at.localeCompare(b.proposed_at),
        )[0]
      : null;

  return (
    <section>
      <h1>{teamName}</h1>

      {urlMismatch && (
        <p role="alert" className="muted">
          <small>
            URL team <code>{routeTeamId.slice(0, 8)}…</code> doesn't match your
            session team <code>{session.team_id.slice(0, 8)}…</code>. Showing
            YOUR team's data.
          </small>
        </p>
      )}

      {/* ─── Health line ────────────────────────────────────────────── */}
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        <small>
          Upstreams: {healthBadge("WTTA", h.wtta)} ·{" "}
          {healthBadge("TennisLink", h.tennislink)} ·{" "}
          {healthBadge("Schmidt", h.schmidt)} ·{" "}
          {healthBadge("Bridge", h.imessage)}
        </small>
      </p>

      {/* ─── Upcoming matches ───────────────────────────────────────── */}
      <h2 style={{ marginTop: "1.5rem" }}>Upcoming matches (14 days)</h2>
      {data.upcoming_matches_14d.length === 0 ? (
        <p className="muted">No matches scheduled.</p>
      ) : (
        <table
          className="responsive-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "0.5rem",
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid #e5e5e5", textAlign: "left" }}>
              <th style={{ padding: "0.5rem 0.5rem 0.5rem 0" }}>Date</th>
              <th style={{ padding: "0.5rem" }}>Opponent</th>
              <th style={{ padding: "0.5rem" }}>Home/Away</th>
              <th style={{ padding: "0.5rem" }}>Lineup</th>
              <th style={{ padding: "0.5rem" }}>RSVPs outstanding</th>
            </tr>
          </thead>
          <tbody>
            {data.upcoming_matches_14d.map((m) => (
              <tr key={m.match_id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                <td
                  data-label="Date"
                  style={{ padding: "0.5rem 0.5rem 0.5rem 0" }}
                >
                  {formatMatchDate(m.date)}
                </td>
                <td data-label="Opponent" style={{ padding: "0.5rem" }}>
                  {m.opponent ?? "—"}
                </td>
                {/* Home/Away derivation lands when matches carry a venue field.
                    For chunk 6 we render an em-dash placeholder rather than
                    invent the data. */}
                <td
                  data-label="Home/Away"
                  style={{ padding: "0.5rem" }}
                  className="muted"
                >
                  —
                </td>
                <td data-label="Lineup" style={{ padding: "0.5rem" }}>
                  <code>{m.lineup_state}</code>
                </td>
                <td
                  data-label="RSVPs outstanding"
                  style={{ padding: "0.5rem" }}
                >
                  {m.rsvps_outstanding}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ─── Pending lineups summary ────────────────────────────────── */}
      <h2 style={{ marginTop: "1.5rem" }}>Pending lineups</h2>
      {data.lineups_pending_approval.length === 0 ? (
        <p className="muted">None pending approval.</p>
      ) : (
        <p>
          <strong>{data.lineups_pending_approval.length}</strong> lineup
          {data.lineups_pending_approval.length === 1 ? "" : "s"} awaiting your
          approval
          {soonestPending && (
            <>
              {" "}
              (soonest proposed{" "}
              {new Date(soonestPending.proposed_at).toLocaleDateString()})
            </>
          )}
          .
        </p>
      )}

      {/* ─── Scores overdue ─────────────────────────────────────────── */}
      <h2 style={{ marginTop: "1.5rem" }}>Scores overdue</h2>
      {data.scores_overdue.length === 0 ? (
        <p className="muted">None overdue.</p>
      ) : (
        <>
          <p>
            <strong>{data.scores_overdue.length}</strong> match
            {data.scores_overdue.length === 1 ? "" : "es"} need a score reported.
          </p>
          <ul>
            {data.scores_overdue.map((s) => (
              <li key={s.match_id}>
                <code>{s.match_id.slice(0, 8)}…</code> — {s.hours_overdue}h
                overdue
              </li>
            ))}
          </ul>
        </>
      )}

      {/* ─── Recent scouting briefs ─────────────────────────────────── */}
      <h2 style={{ marginTop: "1.5rem" }}>Recent scouting briefs</h2>
      {data.recent_scouting_briefs.length === 0 ? (
        <p className="muted">No briefs yet. Try /scout in iMessage.</p>
      ) : (
        <div>
          {data.recent_scouting_briefs.map((b) => (
            <details
              key={`${b.match_id}-${b.generated_at}`}
              style={{ marginTop: "0.5rem" }}
            >
              <summary style={{ cursor: "pointer", padding: "0.25rem 0" }}>
                vs {b.opponent ?? "—"} —{" "}
                {new Date(b.generated_at).toLocaleDateString()}
              </summary>
              <pre
                style={{
                  background: "#f6f6f6",
                  padding: "0.75rem",
                  borderRadius: 4,
                  overflowX: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "ui-monospace, monospace",
                  fontSize: "0.9rem",
                }}
              >
                {b.body_md}
              </pre>
            </details>
          ))}
        </div>
      )}
    </section>
  );
}
